/**
 * Single source of truth for mapping a backend match-with `valueType`
 * (from `/api/application/conditionAttribute`'s `matchWithElement[]`) to a
 * generic-ConditionBuilder `MatchWithOption`.
 *
 * Used by BOTH:
 *  - the config hook's `resolveOperator` (live builder — names come from the
 *    backend `matchWithElement.name`), and
 *  - the read util `mapPolicyFormValues` (edit mode — only `valueType` is
 *    persisted, so the untranslated `MATCH_WITH_DEFAULT_NAME` fallback is used
 *    and later reconciled by the live `resolveOperator` result).
 *
 * The `MatchWithOption.id` carries the SEMANTIC valueType (what is written back
 * to the API), while `MatchWithOption.valueType` carries the RENDER primitive
 * the value field uses. This mirrors the legacy domain-policy convention in
 * `src/pages/Accounts/Details/DomainPolicy/Form/ColumnRuleItem.tsx`
 * (`id: matchWith.valueType`, `name: matchWith.name`).
 */
import { UserEntitlementAttributeControllerService } from "@/api";
import {
  ConditionOption,
  MatchWithOption,
} from "@/components/ConditionBuilder/types";

/** Semantic match-with value types (the API `valueType` strings). */
export const MCP_MATCH_WITH = {
  CONSTANT: "CONSTANT",
  CUSTOM_VALUE: "CUSTOM_VALUE",
  CUSTOM_EXPRESSION: "CUSTOM_EXPRESSION",
  VALUE: "VALUE",
  USER_ATTRIBUTE: "USER_ATTRIBUTE",
  AGENT_ATTRIBUTE: "AGENT_ATTRIBUTE",
  ASSET_ATTRIBUTE: "ASSET_ATTRIBUTE",
  EXTERNAL_FUNCTION: "EXTERNAL_FUNCTION",
} as const;

/**
 * Untranslated display labels, used only where the backend `name` is absent —
 * notably the pure read path (edit mode), which persists only `valueType`.
 * Follows the same pattern as `OPERATOR_DEFAULT_NAME` in operatorMappings.ts:
 * the seeded label is display-only and is overwritten by the live option list
 * once the attribute/operator resolves.
 */
export const MATCH_WITH_DEFAULT_NAME: Record<string, string> = {
  [MCP_MATCH_WITH.CONSTANT]: "Value",
  [MCP_MATCH_WITH.CUSTOM_VALUE]: "Custom Value",
  [MCP_MATCH_WITH.CUSTOM_EXPRESSION]: "Custom Expression",
  [MCP_MATCH_WITH.VALUE]: "Constant Value",
  [MCP_MATCH_WITH.USER_ATTRIBUTE]: "User Attribute",
  [MCP_MATCH_WITH.AGENT_ATTRIBUTE]: "Agent Attribute",
  [MCP_MATCH_WITH.ASSET_ATTRIBUTE]: "Asset Attribute",
  [MCP_MATCH_WITH.EXTERNAL_FUNCTION]: "External Function",
};

/** i18n key per match-with value type, for the live builder's option labels. */
export const MATCH_WITH_LABEL_KEY: Record<string, string> = {
  [MCP_MATCH_WITH.CONSTANT]: "mcpRegistry.conditionBuilder.matchWith.value",
  [MCP_MATCH_WITH.CUSTOM_VALUE]:
    "mcpRegistry.conditionBuilder.matchWith.customValue",
  [MCP_MATCH_WITH.CUSTOM_EXPRESSION]:
    "mcpRegistry.conditionBuilder.matchWith.customExpression",
  [MCP_MATCH_WITH.VALUE]:
    "mcpRegistry.conditionBuilder.matchWith.constantValue",
  [MCP_MATCH_WITH.USER_ATTRIBUTE]:
    "mcpRegistry.conditionBuilder.matchWith.userAttribute",
  [MCP_MATCH_WITH.AGENT_ATTRIBUTE]:
    "mcpRegistry.conditionBuilder.matchWith.agentAttribute",
  [MCP_MATCH_WITH.ASSET_ATTRIBUTE]:
    "mcpRegistry.conditionBuilder.matchWith.assetAttribute",
  [MCP_MATCH_WITH.EXTERNAL_FUNCTION]:
    "mcpRegistry.conditionBuilder.matchWith.externalFunction",
};

interface BuildMatchWithOptionArgs {
  /** LHS attribute id — used to scope the CONSTANT value masterdata fetch. */
  attributeId: string;
  /** Whether the LHS attribute accepts multiple values (CONSTANT picker mode). */
  isMultiValue: boolean;
  /** Backend-provided display label; falls back to MATCH_WITH_DEFAULT_NAME. */
  name?: string;
  /**
   * Supplies the selectable attribute list for the attribute-picker types.
   * The caller returns the columns for the requested group. Omitted on the
   * pure read path (the live resolveOperator fills the options; the saved
   * value still renders from its {id,name} fallback).
   */
  attributePickerOptions?: (
    groupId: "USER" | "AGENT" | "ASSET",
  ) => ConditionOption[];
}

/**
 * Maps one backend match-with `valueType` to a `MatchWithOption`.
 *
 * - CUSTOM_VALUE / VALUE (labelled "Constant Value" — see MATCH_WITH_LABEL_KEY)
 *   → freeform text input.
 * - USER_ATTRIBUTE / AGENT_ATTRIBUTE → single-select of that group's attributes.
 * - CONSTANT (labelled "Value") and any unknown type → value picker backed by
 *   the attribute's masterdata (single- or multi-select per `isMultiValue`).
 */
export function buildMcpMatchWithOption(
  valueType: string,
  {
    attributeId,
    isMultiValue,
    name,
    attributePickerOptions,
  }: BuildMatchWithOptionArgs,
): MatchWithOption {
  const label = name ?? MATCH_WITH_DEFAULT_NAME[valueType] ?? valueType;

  switch (valueType) {
    case MCP_MATCH_WITH.EXTERNAL_FUNCTION:
    case MCP_MATCH_WITH.CUSTOM_EXPRESSION:
    case MCP_MATCH_WITH.CUSTOM_VALUE:
    case MCP_MATCH_WITH.VALUE:
      // VALUE (labelled "Constant Value"): author types a constant matched to
      // the LHS data type (string or number). Distinct id from CONSTANT (which
      // is labelled "Value" and owns the masterdata value-picker instead) + text
      // primitive so the value field renders a freeform input here. The backend
      // (RegoPolicyService) quotes/raws by the LHS data type; the UI just needs
      // freeform text for both.
      return { id: valueType, name: label, valueType: "text" };

    case MCP_MATCH_WITH.USER_ATTRIBUTE:
    case MCP_MATCH_WITH.AGENT_ATTRIBUTE:
    case MCP_MATCH_WITH.ASSET_ATTRIBUTE:
      return {
        id: valueType,
        name: label,
        valueType: isMultiValue ? "multi-select" : "single-select",
        valueOptions:
          attributePickerOptions?.(
            valueType === MCP_MATCH_WITH.AGENT_ATTRIBUTE
              ? "AGENT"
              : valueType === MCP_MATCH_WITH.ASSET_ATTRIBUTE
                ? "ASSET"
                : "USER",
          ) ?? [],
      };

    case MCP_MATCH_WITH.CONSTANT:
    default:
      // Preserve the real `valueType` as the id so unknown/new backend types
      // round-trip on save instead of being silently coerced to CONSTANT.
      // For the genuine CONSTANT case `valueType === "CONSTANT"`, so this is
      // unchanged; both still render as the masterdata value picker.
      return {
        id: valueType,
        name: label,
        valueType: isMultiValue ? "multi-select" : "single-select",
        paginatedFetchOptions: {
          method:
            UserEntitlementAttributeControllerService.getUserAttributeMasterDataUsingGet,
          params: { attributeId },
          parseOption: (item: { value?: string | null }) =>
            item.value ? { id: item.value, name: item.value } : null,
          perPage: 20,
          sort: { field: "name", dir: "asc" as const },
        },
      };
  }
}
